import { useCallback, useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';
import type { MineralType } from 'types/minerals';
import { STREAM_ORDER, useDcaStreamNavigation } from '../../hooks';
import { selectedFitStreamsAtom } from '../../atoms';
import {
  dcaHotkeysHelpOpenAtom,
  dcaHotkeysModeAtom,
  dcaSaveRequestAtom,
} from './atoms';

/** The stream each hotkey scope drives, in the accordion's top-to-bottom order. */
const SCOPE_STREAMS: Record<string, MineralType> = {
  DCA_INPUT_FORM_OIL: 'oil',
  DCA_INPUT_FORM_GAS: 'gas',
  DCA_INPUT_FORM_WATER: 'water',
};

/** Arguments to `useDcaHotkeysMode`. */
export interface DcaHotkeysModeOptions {
  /** Hotkeys mode only binds inside the DCA tearsheet. */
  enabled: boolean;
  /** Walk to the previous/next well in the wells-table display order. */
  onPreviousWell?: () => void;
  onNextWell?: () => void;
  /** Shift the selected stream's start date by whole months. */
  onShiftStartDate?: (months: number) => void;
  /** Run the regression over the checked streams. */
  onFitCheckedStreams?: () => void;
  /**
   * Show only the selected stream on the charts, or bring the others back.
   */
  onToggleStreamSolo?: () => void;
}

/**
 * Binds the hotkeys for the DCA tearsheet: entering and leaving hotkeys mode,
 * switching between the Insights and ARIES parameter mappings, well and stream
 * navigation, shifting the start date, checking a stream for regression,
 * fitting, saving, and the cheatsheet.
 *
 * `useDcaVolumeHotkeys` binds the parameter keys themselves. This hook only
 * decides whether they are enabled and which mapping they use.
 *
 * @param {DcaHotkeysModeOptions} options - Whether to bind, plus the actions
 *   the tearsheet supplies.
 * @return {object} The current mode, functions to enter and leave it, and the
 *   hotkey scope of the selected stream.
 */
export const useDcaHotkeysMode = ({
  enabled,
  onPreviousWell,
  onNextWell,
  onShiftStartDate,
  onFitCheckedStreams,
  onToggleStreamSolo,
}: DcaHotkeysModeOptions) => {
  const [mode, setMode] = useAtom(dcaHotkeysModeAtom);
  const setHelpOpen = useSetAtom(dcaHotkeysHelpOpenAtom);
  const setSaveRequest = useSetAtom(dcaSaveRequestAtom);
  const [selectedFitStreams, setSelectedFitStreams] = useAtom(
    selectedFitStreamsAtom
  );
  const { activeScopes, toggleScope, disableScope } = useHotkeysContext();
  // Owns the keys that move between streams. It has to be called exactly once
  // while the mode is mounted: react-hotkeys-hook does not merge two
  // registrations of the same key, so a second call would run the handler twice
  // for one press and the two toggles would cancel out.
  useDcaStreamNavigation();

  // Leaving the mode releases the stream too, so the panel's hotkeys button
  // stops reading as on when no key does anything.
  const releaseStreams = useCallback(() => {
    STREAM_ORDER.forEach((scope) => disableScope(scope));
  }, [disableScope]);

  // Closing the tearsheet turns the mode off.
  useEffect(() => {
    if (enabled) return;
    setMode((prev) => (prev.active ? { ...prev, active: false } : prev));
    setHelpOpen(false);
    releaseStreams();
  }, [enabled, setMode, setHelpOpen, releaseStreams]);

  const activeStream = STREAM_ORDER.find((scope) =>
    activeScopes.includes(scope)
  );

  const enterMode = useCallback(() => {
    setMode((prev) => ({ ...prev, active: true }));
    // With no stream selected the parameter keys would do nothing, so select
    // oil, the first accordion.
    if (!STREAM_ORDER.some((scope) => activeScopes.includes(scope))) {
      toggleScope(STREAM_ORDER[0]);
    }
  }, [setMode, activeScopes, toggleScope]);

  const exitMode = useCallback(() => {
    setMode((prev) => ({ ...prev, active: false }));
    setHelpOpen(false);
    releaseStreams();
  }, [setMode, setHelpOpen, releaseStreams]);

  const options = { enabled, enableOnFormTags: true, preventDefault: true };
  const modeOptions = {
    enabled: enabled && mode.active,
    enableOnFormTags: true,
    preventDefault: true,
  };

  useHotkeys('mod+k', enterMode, options, [enterMode]);
  useHotkeys('esc', exitMode, modeOptions, [exitMode]);
  useHotkeys('mod+h', () => setHelpOpen((open) => !open), modeOptions, [
    setHelpOpen,
  ]);

  useHotkeys(
    'mod+i',
    () => setMode((prev) => ({ ...prev, mapping: 'insights' })),
    modeOptions,
    [setMode]
  );
  useHotkeys(
    'mod+a',
    () => setMode((prev) => ({ ...prev, mapping: 'aries' })),
    modeOptions,
    [setMode]
  );

  useHotkeys('shift+left', () => onPreviousWell?.(), modeOptions, [
    onPreviousWell,
  ]);
  useHotkeys('shift+right', () => onNextWell?.(), modeOptions, [onNextWell]);

  // Checks the stream whose accordion is selected, so Shift+F fits it next.
  useHotkeys(
    'shift+s',
    () => {
      const stream = activeStream ? SCOPE_STREAMS[activeStream] : undefined;
      if (!stream) return;
      setSelectedFitStreams(
        selectedFitStreams.includes(stream)
          ? selectedFitStreams.filter((s) => s !== stream)
          : [...selectedFitStreams, stream]
      );
    },
    modeOptions,
    [activeStream, selectedFitStreams, setSelectedFitStreams]
  );

  useHotkeys('shift+f', () => onFitCheckedStreams?.(), modeOptions, [
    onFitCheckedStreams,
  ]);

  useHotkeys('mod+e', () => onToggleStreamSolo?.(), modeOptions, [
    onToggleStreamSolo,
  ]);

  // Alt on Windows, Cmd on Mac. 'mod' means Cmd on Mac and Ctrl elsewhere, so
  // both are bound and only one is reachable on a given platform.
  useHotkeys(
    ['alt+left', 'mod+left'],
    () => onShiftStartDate?.(-1),
    modeOptions,
    [onShiftStartDate]
  );
  useHotkeys(
    ['alt+right', 'mod+right'],
    () => onShiftStartDate?.(1),
    modeOptions,
    [onShiftStartDate]
  );

  useHotkeys('mod+s', () => setSaveRequest((count) => count + 1), modeOptions, [
    setSaveRequest,
  ]);

  return { mode, enterMode, exitMode, activeStream };
};
